import * as React from "react";
import { Row, Col, DatePicker, Popconfirm, Form } from "antd";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { dayjs } from "Config/dayjs";
import { Button } from "Components/General/Button";
import { modalProps } from "../utils/modalProps";
import { ModalConsumer } from "Components/General/Modal/ModalContext";
import { AddUser } from "./AddUser";
import { AddGroup } from "./AddGroup";
import { DataFilterTable } from "Components/Form/DataFilterTable";
import { ColumnProps } from "antd/lib/table";
import { stringSort } from "utils/defaultSort";
import { contactStore } from "Pages/UserSettings/Stores/ContactStore";
import { userInfoStore } from "Stores";
import { UserAndContactSelection } from "Components/Form/UserAndContactSelection";
import { contactsStore } from "Stores/ContactsStore";
import { formatUsername } from "utils/formatUsername";

interface RecipientDeliveryProps extends WithTranslation {}

interface RecipientSelectionProps extends WithTranslation {
  value?: API_PrintJobRecipientWithfirstName[];
  onChange?: (value: API_PrintJobRecipientWithfirstName[]) => API_PrintJobRecipientWithfirstName[];
}

interface TableSorter {
  columnKey: string;
  order: "ascend" | "descend";
}

export interface API_PrintJobRecipientWithfirstName extends API_PrintJobRecipient {
  firstName?: string;
}

interface RecipientSelectionState {
  data: API_PrintJobRecipientWithfirstName[];
  sortedInfo: TableSorter;
}

class RecipientColumn extends React.Component<ColumnProps<API_PrintJobRecipient>> {}

const RecipientSelection = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<RecipientSelectionProps, RecipientSelectionState> {
      constructor(props: RecipientSelectionProps) {
        super(props);
        this.state = {
          data: [],
          sortedInfo: {
            columnKey: "selected",
            order: "descend",
          },
        };
      }

      componentDidMount() {
        this.addDefaultRecipient();
      }

      removeDuplicatesRecipients = (recipients: API_PrintJobRecipient[]) => {
        const result = [];
        const map = new Map();

        for (const item of recipients) {
          const key = item.id + "_" + (item.external ? "1" : "0");
          if (!map.has(key)) {
            map.set(key, true);
            result.push(item);
          }
        }

        return result;
      };

      addDefaultRecipient = () => {
        const { onChange } = this.props;
        const { firstname, surname, companyName, street, postalCode, city, email } = contactStore.data;
        const { id } = userInfoStore;
        const data = this.removeDuplicatesRecipients(
          this.state.data.concat({
            id,
            company: companyName,
            email,
            location: city,
            postalCode,
            street,
            name: formatUsername({ firstName: firstname, surname: surname }),
            firstName: firstname,
            external: false,
          })
        );

        try {
          this.setState({ data }, () => onChange(this.state.data));
        } catch (error) {
          throw error;
        }
      };

      handleAdd = async (records: (API_User | API_Contact)[]) => {
        const { onChange } = this.props;

        const reciptions: API_PrintJobRecipientWithfirstName[] = [];
        for (const record of records) {
          // if has contactType - most likely it's a contact :)
          if ((record as API_Contact).contactType) {
            const { name, email, company, street, location, postalCode, id } = contactsStore.globalUserContacts.find(
              (u) => record.id === u.id
            );

            reciptions.push({ name, email, company, street, postalCode, location, id, firstName: "", external: true });
            // expected to be user
          } else {
            const { surname, firstName, email, companyName, street, city, postalCode, id } =
              contactsStore.projectUsers.find((u) => record.id === u.id);
            reciptions.push({
              name: formatUsername({ firstName: firstName, surname: surname }),
              email,
              company: companyName,
              street,
              postalCode,
              location: city,
              id,
              firstName,
              external: false,
            });
          }
        }

        const data = this.removeDuplicatesRecipients(this.state.data.concat(reciptions));

        try {
          this.setState({ data }, () => onChange(this.state.data));
        } catch (error) {
          throw error;
        }
      };

      handleManualAdd = (value: API_PrintJobRecipient) => {
        const { onChange } = this.props;
        const data = this.removeDuplicatesRecipients(this.state.data.concat(value));

        try {
          this.setState({ data }, () => onChange(this.state.data));
        } catch (error) {
          throw error;
        }
      };

      handleDelete = (record) => {
        const { onChange } = this.props;
        const data = [...this.state.data].filter(
          (contact) => !(contact.id === record.id && contact.external === record.external)
        );

        // [...this.state.data].forEach(contact => {
        //   if (contact.children) {
        //     contact.children = contact.children.filter(item => item.id !== record.id);
        //   }

        // });

        this.setState({ data }, () => onChange(this.state.data));
      };

      renderActionButtons = () => {
        const { t } = this.props;
        return (
          <>
            <ModalConsumer>
              {({ showModal }) => (
                <Button
                  style={{ margin: "0 10px" }}
                  type="primary"
                  grey={true}
                  faIcon={["fas", "plus"]}
                  onClick={() =>
                    showModal(UserAndContactSelection, {
                      modalProps: { ...modalProps, zIndex: 1013, width: "100%", style: { maxWidth: 800 } },
                      componentProps: { onSubmit: this.handleAdd },
                    })
                  }
                >
                  {t("Add Users or Global contacts")}
                </Button>
              )}
            </ModalConsumer>
            <ModalConsumer>
              {({ showModal }) => (
                <Button
                  style={{ margin: "0 10px" }}
                  type="primary"
                  grey={true}
                  faIcon={["fas", "plus"]}
                  onClick={() =>
                    showModal(AddUser, {
                      modalProps: { ...modalProps, zIndex: 1013, width: "100%", style: { maxWidth: 800 } },
                      componentProps: { addNewUser: this.handleManualAdd },
                    })
                  }
                >
                  {t("Add other recipient")}
                </Button>
              )}
            </ModalConsumer>
            <ModalConsumer>
              {({ showModal }) => (
                <Button
                  type="primary"
                  faIcon={["fas", "plus"]}
                  onClick={() =>
                    showModal(AddGroup, {
                      modalProps: { ...modalProps, zIndex: 1013, width: "100%", style: { maxWidth: 800 } },
                      componentProps: { addNewContactsGroup: this.handleAdd },
                    })
                  }
                >
                  {t("Add group")}
                </Button>
              )}
            </ModalConsumer>
          </>
        );
      };

      render() {
        const { sortedInfo } = this.state;
        const { t, value } = this.props;
        return (
          <>
            <Row justify="space-between" className="mb-lg">
              <Col>{t("Total: {{ amount }} recipients", { amount: value.length })}</Col>
              <Col>{this.renderActionButtons()}</Col>
            </Row>
            <DataFilterTable
              name="RecipientDelivery"
              rowKey={(record) => Math.random() + record.id.toString(36)}
              dataSource={this.state.data}
              onChange={this.handleChange}
              showSearch={false}
            >
              <RecipientColumn
                title={t("Name")}
                dataIndex={"name"}
                defaultSortOrder={"ascend"}
                sorter={(a, b) => stringSort(a, b, "name")}
                sortOrder={sortedInfo.columnKey === "name" ? sortedInfo.order : undefined}
              />
              <RecipientColumn
                title={t("Company")}
                dataIndex={"company"}
                sorter={(a, b) => stringSort(a, b, "company")}
                sortOrder={sortedInfo.columnKey === "company" ? sortedInfo.order : undefined}
              />
              <RecipientColumn
                title={t("Street")}
                dataIndex={"street"}
                sorter={(a, b) => stringSort(a, b, "street")}
                sortOrder={sortedInfo.columnKey === "street" ? sortedInfo.order : undefined}
              />
              <RecipientColumn
                title={t("Post code")}
                dataIndex={"postalCode"}
                sorter={(a, b) => stringSort(a, b, "postalCode")}
                sortOrder={sortedInfo.columnKey === "postalCode" ? sortedInfo.order : undefined}
              />
              <RecipientColumn
                title={t("City")}
                dataIndex={"location"}
                sorter={(a, b) => stringSort(a, b, "location")}
                sortOrder={sortedInfo.columnKey === "location" ? sortedInfo.order : undefined}
              />
              <RecipientColumn
                title={t("Email")}
                dataIndex={"email"}
                sorter={(a, b) => stringSort(a, b, "email")}
                sortOrder={sortedInfo.columnKey === "email" ? sortedInfo.order : undefined}
              />
              <RecipientColumn
                title={""}
                dataIndex={"operation"}
                render={(text, record) => {
                  return this.state.data.length >= 1 ? (
                    <Popconfirm
                      title={t("Are you sure?")}
                      okText={t("Yes")}
                      cancelText={t("No")}
                      onConfirm={() => this.handleDelete(record)}
                    >
                      <a>X</a>
                    </Popconfirm>
                  ) : null;
                }}
              />
            </DataFilterTable>
          </>
        );
      }

      private handleChange = (pagination: {}, filters: {}, sorter: TableSorter) => {
        this.setState({
          sortedInfo: sorter,
        });
      };
    }
  )
);

const today = dayjs().startOf("day");

export const RecipientDelivery = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<RecipientDeliveryProps> {
      disabledDate = (current: dayjs.Dayjs) => current && current.isBefore(today);

      render() {
        const { t } = this.props;

        return (
          <Row justify="start" align="middle" gutter={24}>
            <Col span={12} className="mb-lg">
              <Form.Item label={t("Delivery date")} name="deliveryDate">
                <DatePicker disabledDate={this.disabledDate} format="l" placeholder={t("Delivery date")} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name={"recipients"}
                initialValue={[]}
                rules={[
                  {
                    required: true,
                    message: t("Please provide at least one recipient."),
                  },
                ]}
              >
                <RecipientSelection />
              </Form.Item>
            </Col>
            <Col span={24}>
              <span style={{ fontSize: "13px", color: "#8992a1" }}>
                *{" "}
                {t(
                  `Duplicates of users who were added from the project or group will be automatically removed from the list of recipients.`
                )}
              </span>
            </Col>
          </Row>
        );
      }
    }
  )
);
